import { useCallback, useEffect, useMemo, useState } from 'react';
import { regionService } from '../../../services';

const AREA_KEY = 'pr.curatorArea';
export const RADIUS_OPTIONS = [10, 25, 50, 100, 250];

function readSavedArea() {
  try {
    const raw = localStorage.getItem(AREA_KEY);
    const a = raw ? JSON.parse(raw) : null;
    return a && Number.isFinite(a.lat) && Number.isFinite(a.lng) ? a : null;
  } catch { return null; }
}

/** Miles between two coordinates — the AREA's own distance column and city suggestions. */
export function milesBetween(aLat, aLng, bLat, bLng) {
  if (![aLat, aLng, bLat, bLng].every(Number.isFinite)) return null;
  const R = 3958.8;
  const dLat = ((bLat - aLat) * Math.PI) / 180;
  const dLng = ((bLng - aLng) * Math.PI) / 180;
  const la = (aLat * Math.PI) / 180;
  const lb = (bLat * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la) * Math.cos(lb) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * The AREA (city + radius) shared by every geo-scoped section — EVENTS, PICK EVENTS,
 * FEATURED, PARTY PATRONS, PARTY ENGINE, LISTED PROS — remembered per person at
 * `pr.curatorArea`, exactly as the old curator page did. A city outside every region's
 * box resolves to `region: null`, which is an answer, not an error.
 *
 * There are two states: the **draft** the person is typing into the bar, and the
 * **applied** area the section queries with. `pull()` promotes the draft; until then
 * `dirty` is true and the bar's status dot is amber.
 */
export default function useArea() {
  const saved = useMemo(() => readSavedArea(), []);
  const [area, setAreaState] = useState(saved ? { label: saved.label, lat: saved.lat, lng: saved.lng } : null);
  const [radiusMiles, setRadiusMiles] = useState(saved?.radiusMiles || 50);
  const [draft, setDraft] = useState(saved ? { label: saved.label, lat: saved.lat, lng: saved.lng } : null);
  const [draftRadius, setDraftRadius] = useState(saved?.radiusMiles || 50);
  const [region, setRegion] = useState(null);
  const [resolving, setResolving] = useState(false);

  /** Apply at once — what a page does when it sets the area itself. */
  const setArea = useCallback((a) => {
    const next = a ? { label: a.label, lat: a.lat, lng: a.lng, region: a.region } : null;
    setAreaState(next);
    setDraft(next);
  }, []);

  const setRadius = useCallback((m) => { setRadiusMiles(m); setDraftRadius(m); }, []);

  /** Promote the draft. Returns the area that is now live. */
  const pull = useCallback(() => {
    setAreaState(draft ? { ...draft } : null);
    setRadiusMiles(draftRadius);
    return draft;
  }, [draft, draftRadius]);

  const dirty = (draft?.label || null) !== (area?.label || null) || draftRadius !== radiusMiles;

  useEffect(() => {
    try {
      if (area) localStorage.setItem(AREA_KEY, JSON.stringify({ ...area, radiusMiles }));
      else localStorage.removeItem(AREA_KEY);
    } catch { /* private mode */ }
  }, [area, radiusMiles]);

  useEffect(() => {
    let cancelled = false;
    if (!area) { setRegion(null); return undefined; }
    setResolving(true);
    (async () => {
      const r = await regionService.forCoords(area.lat, area.lng);
      if (cancelled) return;
      setRegion(r.success ? r.data : null);
      setResolving(false);
    })();
    return () => { cancelled = true; };
  }, [area]);

  return {
    area, setArea, radiusMiles, setRadiusMiles: setRadius, region, resolving,
    draft, setDraft, draftRadius, setDraftRadius, pull, dirty,
  };
}
